import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useSession } from '../App.jsx';
import { day, inr, num, qty, stamp } from '../lib/format.js';
import { ROLES, can } from '../lib/roles.js';
import { Alert, Card, CardSkeleton, Pill, useAsync } from '../components/ui.jsx';
import MarkInTally from '../components/MarkInTally.jsx';
import { WhatsAppButton } from './InvoiceDetail.jsx';

const REASONS = {
  sales_return: 'Sales return', rate_difference: 'Rate difference',
  post_sale_discount: 'Post-sale discount', other: 'Other',
};

export default function CreditNoteDetail() {
  const { id } = useParams();
  const cn = useAsync(() => api.get(`/api/credit-notes/${id}`), [id]);
  const [error, setError] = useState(null);
  const session = useSession();
  const canCancel = can(session, ROLES.ADMIN_ACCOUNTS);

  const print = async () => {
    setError(null);
    try { await api.open(`/api/credit-notes/${id}/pdf`); } catch (e) { setError(e.message); }
  };

  const cancel = async () => {
    if (!window.confirm(`Cancel credit note ${cn.data.cn_number || '(draft)'}? This cannot be undone.`)) return;
    setError(null);
    try { await api.post(`/api/credit-notes/${id}/cancel`, { reason: 'Raised in error' }); cn.reload(); }
    catch (e) { setError(e.message); }
  };

  if (cn.loading && !cn.data) return <CardSkeleton lines={5} />;
  if (cn.error) return <Alert onRetry={cn.reload}>{cn.error}</Alert>;
  const c = cn.data;

  return (
    <>
      <div className="page-head">
        <div>
          <div className="crumb" style={{ marginBottom: 6 }}>
            <Link to="/credit-notes">Credit notes</Link> / <b>{c.cn_number || 'Draft'}</b>
          </div>
          <h1 className="page-title">{inr(c.grand_total)}</h1>
          <div className="page-sub">
            {REASONS[c.reason] || c.reason} against invoice <Link to={`/invoices/${c.invoice_id}`}>{c.invoice_number}</Link>
            {' '}· {c.buyer_name} · {day(c.cn_date)}
          </div>
        </div>
        <div className="inline">
          <button className="btn secondary" onClick={print}>Print / PDF</button>
          {c.status === 'issued' && can(session, ROLES.COMMERCIAL)
            && (session.allEntities || session.entities).some((e) => e.id === c.entity_id && e.whatsapp_enabled) && (
            <WhatsAppButton docType="credit_note" id={c.id} />
          )}
          {canCancel && c.status !== 'cancelled' && c.tally_sync_status !== 'synced' && (
            <button className="btn secondary" onClick={cancel}>Cancel</button>
          )}
          {canCancel && c.status === 'issued' && ['skipped', 'failed'].includes(c.tally_sync_status) && (
            <MarkInTally docType="credit_note" id={c.id} onDone={cn.reload} />
          )}
        </div>
      </div>

      <Alert>{error}</Alert>
      {c.tally_error && <Alert kind="warn">Tally: {c.tally_error}</Alert>}

      <div className="grid halves" style={{ marginBottom: 16 }}>
        <Card title="Document">
          <div className="row">
            <div><div className="label">Status</div><div style={{ marginTop: 4 }}><Pill value={c.status} /></div></div>
            <div><div className="label">Tally</div><div style={{ marginTop: 4 }}><Pill value={c.tally_sync_status} /></div>
              {c.tally_voucher_no && <div className="sub">voucher #{c.tally_voucher_no} · {stamp(c.tally_synced_at)}</div>}</div>
            <div><div className="label">Goods returned</div><div style={{ marginTop: 4 }}>{c.goods_returned ? 'Yes' : 'No — value only'}</div></div>
            <div><div className="label">Place of supply</div><div style={{ marginTop: 4 }}>{c.place_of_supply}</div></div>
          </div>
          {c.narration && <div className="card-note" style={{ marginTop: 10 }}>{c.narration}</div>}
        </Card>
        <Card title="Amounts">
          <table>
            <tbody>
              <tr><td>Taxable value</td><td className="r num">{inr(c.taxable_value)}</td></tr>
              {c.is_interstate
                ? <tr><td>IGST</td><td className="r num">{inr(c.igst_amount)}</td></tr>
                : (<>
                  <tr><td>CGST</td><td className="r num">{inr(c.cgst_amount)}</td></tr>
                  <tr><td>SGST</td><td className="r num">{inr(c.sgst_amount)}</td></tr>
                </>)}
              <tr><td>Round off</td><td className="r num">{inr(c.round_off)}</td></tr>
              <tr><td><b>Total credited</b></td><td className="r num"><b>{inr(c.grand_total)}</b></td></tr>
            </tbody>
          </table>
          <div className="card-note" style={{ marginTop: 8 }}>{c.amount_in_words}</div>
        </Card>
      </div>

      <Card title="Lines">
        <div className="table-wrap">
          <table>
            <thead><tr><th>#</th><th>Description</th><th className="r">Qty</th><th className="r">Rate</th>
              <th className="r">Taxable</th><th>Returned to</th></tr></thead>
            <tbody>
              {c.lines.map((l) => (
                <tr key={l.id}>
                  <td className="num">{l.line_no}</td>
                  <td>{l.description}<div className="sub">HSN {l.hsn || '—'} · GST {l.gst_rate}%</div></td>
                  <td className="r num">{qty(l.qty, l.uom)}</td>
                  <td className="r num">{inr(l.rate)}</td>
                  <td className="r num">{inr(l.taxable_value)}</td>
                  <td>
                    {l.batches.length === 0 && <span className="muted">no goods</span>}
                    {l.batches.map((b) => (
                      <div key={b.id} className="mono">{b.batch_name} · {qty(b.qty)} <span className="sub">{b.godown_name}</span></div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}
